import React, { useEffect } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Grid2 as Grid, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { CheckboxField, MultiSelect, SwitchField } from 'components/form';
import { Controller, useForm } from 'react-hook-form';
import HelpIcon from '@mui/icons-material/Help';
import {
  PortalConfiguration,
  PortalConfigurationType,
  UserProfile,
  VendorContactRole,
} from '@trnsact/trnsact-shared-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import {
  getLocationsSelectOptions,
  getUserRolesSubSetSelectOptions,
  getUsersSelectOptions,
} from 'utils/selectOptionsHelper';
import { portalConfigurationsActions } from 'redux/portalConfigurationReducer';
import { GET_LOCATIONS, GET_PORTAL_CONFIGURATIONS, UPDATE_VENDOR_PROFILE } from '../api';
import { LocationsReponse, PortalConfigurationsReponse } from '../api/types';
import { OpportunitiesNotificationsForm, OpportunitiesNotificationsStageUpdatesForm } from '../types';
import { PortalConfigurationCardProps } from '../../types';

const DEFAULT_STAGE_UPDATES_USER_ROLES = [
  VendorContactRole.SalesMgr,
  VendorContactRole.SalesRep,
  VendorContactRole.CreditMgr,
];

interface VendorProfileEditableFields {
  autoUpdateStageOnSubmissionApproval: boolean;
}

export const OpportunitiesNotifications = ({
  loading,
  portalConfig,
  defaultConfig,
  vendorProfile,
  onSaveConfiguration,
}: Omit<PortalConfigurationCardProps, 'isSuperAdminOrSingleAccount'>) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const relatedUsers = useSelector((state: any) => state.relatedUsers) as UserProfile[];

  const { control, setValue, reset, handleSubmit, watch } = useForm<OpportunitiesNotificationsForm>({
    defaultValues: {
      ...defaultConfig.jsonDefinition.config,
      autoUpdateStageOnSubmissionApproval: vendorProfile.autoUpdateStageOnSubmissionApproval,
    },
  });

  useQuery<PortalConfigurationsReponse>(GET_PORTAL_CONFIGURATIONS, {
    fetchPolicy: 'no-cache',
    skip: !vendorProfile,
    variables: {
      vendorProfileId: vendorProfile.id,
      types: [PortalConfigurationType.VoNotifications],
    },
    onCompleted(response) {
      const configToSet = response?.portalConfigurations?.[0] || defaultConfig;
      const { stageUpdates } = configToSet.jsonDefinition.config;

      //If turned on but no user roles selected, set default user roles and default to current global logic
      if (stageUpdates.isTurnedOn == true && (!stageUpdates.userRoles || stageUpdates.userRoles.length === 0)) {
        stageUpdates.userRoles = DEFAULT_STAGE_UPDATES_USER_ROLES;
        stageUpdates.isNotifyAllRelatedUsers = true;
      }

      dispatch(portalConfigurationsActions.setConfigData([configToSet]));
      reset(configToSet.jsonDefinition.config as OpportunitiesNotificationsForm);
    },
  });

  const { data: locationsData } = useQuery<LocationsReponse>(GET_LOCATIONS);

  const [updateVP] = useMutation(UPDATE_VENDOR_PROFILE, {
    context: { authRequired: true },
  });

  const userRolesSelectOptions = getUserRolesSubSetSelectOptions();
  const usersSelectOptions = getUsersSelectOptions(relatedUsers);

  const handleMultiSelectChange = (
    selected: (string | number)[],
    key: keyof OpportunitiesNotificationsStageUpdatesForm
  ) => {
    setValue(`stageUpdates.${key}`, selected as string[]);
  };

  const handleSave = async (formData: OpportunitiesNotificationsForm) => {
    try {
      const { autoUpdateStageOnSubmissionApproval, ...pcwConfig } = formData;
      const vendorProfileEditableFields: VendorProfileEditableFields = {
        autoUpdateStageOnSubmissionApproval,
      };
      const configToSave: PortalConfiguration = { ...portalConfig, jsonDefinition: { config: pcwConfig } };
      await onSaveConfiguration(configToSave);
      await updateVP({
        variables: {
          entityId: vendorProfile.id,
          fieldsObj: vendorProfileEditableFields,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const isTurnedOn = watch('stageUpdates.isTurnedOn');
  const isNotifyAllRelatedUsers = watch('stageUpdates.isNotifyAllRelatedUsers');

  useEffect(() => {
    if (isNotifyAllRelatedUsers) {
      setValue('stageUpdates.userRoles', []);
      setValue('stageUpdates.locationIds', []);
      setValue('stageUpdates.userIds', []);
    }
  }, [isNotifyAllRelatedUsers]);

  return (
    <Grid container spacing={2}>
      <Typography component="p" variant="h6">
        Opportunities
      </Typography>

      <Grid size={12}>
        <SwitchField
          control={control}
          name="autoUpdateStageOnSubmissionApproval"
          tooltip="When ON, a lender approval will automatically update the Opportunity Stage to Approved. When OFF, stage progression must be handled manually."
        />
        <Typography component="span" variant="subtitle2">
          Automatically Advance Opportunity Stage on Submission Approval
        </Typography>
      </Grid>

      <Grid size={12}>
        <SwitchField
          control={control}
          name="stageUpdates.isTurnedOn"
          tooltip="Notify the below group(s) when an Opportunity stage changes"
        />
        <Typography component="span" variant="subtitle2">
          Stage Updates
        </Typography>
      </Grid>

      {isTurnedOn && (
        <Grid container spacing={2}>
          <Grid size={12} className={classes.byRoleContainer}>
            <Controller
              control={control}
              name="stageUpdates.userRoles"
              render={({ field }) => (
                <>
                  <CheckboxField
                    control={control}
                    name="stageUpdates.isNotifyAllRelatedUsers"
                    label={'By Assignment Only'}
                  />

                  <Tooltip
                    title={
                      'When enabled defaults to global configuration settings and notify all users assigned to the opportunity based on the above selected user roles.'
                    }
                  >
                    <IconButton size="large">
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>

                  <MultiSelect
                    disabled={isNotifyAllRelatedUsers}
                    label="User role(s):"
                    options={userRolesSelectOptions}
                    className={classes.input}
                    defaultChecked={true}
                    formControlProps={{
                      variant: 'standard',
                    }}
                    {...field}
                    onChange={(selected: (string | number)[]) => handleMultiSelectChange(selected, 'userRoles')}
                  />
                </>
              )}
            />
          </Grid>
          <Grid size={12}>
            <Accordion disabled={isNotifyAllRelatedUsers}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                <Typography>Advanced</Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container>
                  <Controller
                    control={control}
                    name="stageUpdates.locationIds"
                    render={({ field }) => (
                      <MultiSelect
                        label="Location - All users attributed to the following location(s):"
                        options={getLocationsSelectOptions(locationsData?.locations ?? [])}
                        className={classes.input}
                        formControlProps={{
                          variant: 'standard',
                        }}
                        {...field}
                        onChange={(selected: (string | number)[]) => handleMultiSelectChange(selected, 'locationIds')}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="stageUpdates.userIds"
                    render={({ field }) => (
                      <MultiSelect
                        label="Specific User(s)"
                        options={usersSelectOptions}
                        className={classes.input}
                        formControlProps={{
                          variant: 'standard',
                        }}
                        {...field}
                        onChange={(selected: (string | number)[]) => handleMultiSelectChange(selected, 'userIds')}
                      />
                    )}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}

      <Grid size={12}>
        <Button loading={loading} variant="contained" onClick={handleSubmit(handleSave)}>
          Save
        </Button>
      </Grid>
    </Grid>
  );
};

const useStyles = makeStyles({
  input: {
    margin: '16px 0',
  },
  byRoleContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    margin: '12px 0',
    padding: 15,
  },
});
